// @ts-nocheck
/**
 * ドラフトシステム - セッション永続化
 */

import { DRAFT_STORAGE } from '../constants';
import { generateSummary } from './draftManager';

// ========================================
// ストレージAPI
// ========================================

/**
 * ドラフトセッションを保存
 * @param {Object} state - 現在の状態
 * @returns {boolean} 成功/失敗
 */
export const saveDraftSession = (state) => {
  const expiryDays = DRAFT_STORAGE.SESSION_EXPIRY_DAYS;

  const sessionData = {
    meta: {
      sessionId: state.sessionId,
      version: '1.0',
      savedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toISOString(),
    },
    summary: generateSummary(state),
    state: serializeState(state),
  };

  try {
    localStorage.setItem(
      DRAFT_STORAGE.SESSION_KEY,
      JSON.stringify(sessionData)
    );
    console.log('[DraftStorage] Session saved:', sessionData.meta.sessionId);
    return true;
  } catch (e) {
    console.error('[DraftStorage] Save failed:', e);
    return false;
  }
};

/**
 * 保存セッションを読み込み
 * @returns {Object|null} セッションデータ or null
 */
export const loadDraftSession = () => {
  try {
    const data = localStorage.getItem(DRAFT_STORAGE.SESSION_KEY);
    if (!data) return null;

    const sessionData = JSON.parse(data);

    // 有効期限チェック
    if (new Date(sessionData.meta.expiresAt) < new Date()) {
      console.log('[DraftStorage] Session expired, removing');
      clearDraftSession();
      return null;
    }

    return sessionData;
  } catch (e) {
    console.error('[DraftStorage] Load failed:', e);
    return null;
  }
};

/**
 * サマリーのみ取得（ダイアログ表示用）
 * @returns {Object|null} サマリー or null
 */
export const getDraftSummary = () => {
  const session = loadDraftSession();
  if (!session) return null;

  return {
    ...session.summary,
    savedAt: session.meta.savedAt,
    sessionId: session.meta.sessionId,
  };
};

/**
 * 保存セッションを削除
 */
export const clearDraftSession = () => {
  localStorage.removeItem(DRAFT_STORAGE.SESSION_KEY);
  console.log('[DraftStorage] Session cleared');
};

/**
 * セッションが存在するかチェック
 * @returns {boolean}
 */
export const hasSavedSession = () => {
  return loadDraftSession() !== null;
};

/**
 * 保存された状態を復元
 * @param {Object[]} allCards - 全カードデータ（カード情報復元用）
 * @returns {Object|null} 復元されたセッション状態
 */
export const restoreDraftSession = (allCards) => {
  const sessionData = loadDraftSession();
  if (!sessionData) return null;

  try {
    const state = deserializeState(sessionData.state, allCards);
    return state;
  } catch (e) {
    console.error('[DraftStorage] Restore failed:', e);
    return null;
  }
};

// ========================================
// シリアライズ/デシリアライズ
// ========================================

/**
 * 状態をシリアライズ（カードIDのみ保存）
 * @param {Object} state - セッション状態
 * @returns {Object} シリアライズされた状態
 */
const serializeState = (state) => {
  const serialized = {
    sessionId: state.sessionId,
    status: state.status,
    createdAt: state.createdAt,
    config: state.config,
    players: state.players,
    economy: state.economy,

    draft: {
      currentPack: state.draft.currentPack,
      currentRound: state.draft.currentRound,
      direction: state.draft.direction,
      packIndex: state.draft.packIndex,

      // カードIDのみ保存
      packs: {},
      picks: {},
      allPacks: state.draft.allPacks.map(pack =>
        pack.map(card => ({ id: card.id, rarity: card.rarity }))
      ),
    },

    timer: state.timer,
  };

  // パックとピックをシリアライズ
  Object.keys(state.draft.packs).forEach(playerId => {
    serialized.draft.packs[playerId] = state.draft.packs[playerId].map(card => ({
      id: card.id,
      rarity: card.rarity,
    }));
  });

  Object.keys(state.draft.picks).forEach(playerId => {
    serialized.draft.picks[playerId] = state.draft.picks[playerId].map(card => ({
      id: card.id,
      rarity: card.rarity,
    }));
  });

  // デッキ構築状態（存在する場合）
  if (state.deckBuilding) {
    serialized.deckBuilding = {};
    Object.keys(state.deckBuilding).forEach(playerId => {
      const db = state.deckBuilding[playerId];
      serialized.deckBuilding[playerId] = {
        deck: db.deck.map(card => ({ id: card.id, rarity: card.rarity })),
        sideboard: db.sideboard.map(card => ({ id: card.id, rarity: card.rarity })),
        isConfirmed: db.isConfirmed,
      };
    });
  }

  // トーナメント状態（存在する場合）
  if (state.tournament) {
    serialized.tournament = state.tournament;
  }

  return serialized;
};

/**
 * 状態をデシリアライズ（カードIDからカード情報を復元）
 * @param {Object} serialized - シリアライズされた状態
 * @param {Object[]} allCards - 全カードデータ
 * @returns {Object} 復元された状態
 */
const deserializeState = (serialized, allCards) => {
  // カードIDからカード情報を引くマップ
  const cardMap = new Map();
  allCards.forEach(card => cardMap.set(card.id, card));

  /**
   * カード参照を復元
   * @param {Object} cardRef - { id, rarity }
   * @returns {Object} カード情報
   */
  const restoreCard = (cardRef) => {
    const card = cardMap.get(cardRef.id);
    if (!card) {
      console.warn('[DraftStorage] Card not found:', cardRef.id);
      return null;
    }
    return { ...card, rarity: cardRef.rarity };
  };

  const state = {
    sessionId: serialized.sessionId,
    status: serialized.status,
    createdAt: serialized.createdAt,
    config: serialized.config,
    players: serialized.players,
    economy: serialized.economy,
    timer: serialized.timer,

    draft: {
      currentPack: serialized.draft.currentPack,
      currentRound: serialized.draft.currentRound,
      direction: serialized.draft.direction,
      packIndex: serialized.draft.packIndex,
      packs: {},
      picks: {},
      packHistory: [],
      allPacks: serialized.draft.allPacks.map(pack =>
        pack.map(restoreCard).filter(c => c !== null)
      ),
    },
  };

  // パックとピックを復元
  Object.keys(serialized.draft.packs).forEach(playerId => {
    state.draft.packs[playerId] = serialized.draft.packs[playerId]
      .map(restoreCard)
      .filter(c => c !== null);
  });

  Object.keys(serialized.draft.picks).forEach(playerId => {
    state.draft.picks[playerId] = serialized.draft.picks[playerId]
      .map(restoreCard)
      .filter(c => c !== null);
  });

  // デッキ構築状態を復元
  if (serialized.deckBuilding) {
    state.deckBuilding = {};
    Object.keys(serialized.deckBuilding).forEach(playerId => {
      const db = serialized.deckBuilding[playerId];
      state.deckBuilding[playerId] = {
        deck: db.deck.map(restoreCard).filter(c => c !== null),
        sideboard: db.sideboard.map(restoreCard).filter(c => c !== null),
        isConfirmed: db.isConfirmed,
      };
    });
  }

  // トーナメント状態を復元
  if (serialized.tournament) {
    state.tournament = serialized.tournament;
  }

  return state;
};

// ========================================
// ドラフト履歴
// ========================================

/**
 * ドラフト履歴に追加
 * @param {Object} result - ドラフト結果
 */
export const addToHistory = (result) => {
  try {
    const historyStr = localStorage.getItem(DRAFT_STORAGE.HISTORY_KEY);
    const history = historyStr ? JSON.parse(historyStr) : [];

    history.unshift({
      sessionId: result.sessionId,
      finishedAt: new Date().toISOString(),
      playerCount: result.playerCount,
      placement: result.placement,
      cardsWon: result.cardsWon,
    });

    // 最大数を超えたら古いものを削除
    while (history.length > DRAFT_STORAGE.MAX_SAVED_SESSIONS) {
      history.pop();
    }

    localStorage.setItem(DRAFT_STORAGE.HISTORY_KEY, JSON.stringify(history));
  } catch (e) {
    console.error('[DraftStorage] Failed to add history:', e);
  }
};

/**
 * ドラフト履歴を取得
 * @returns {Object[]} 履歴
 */
export const getHistory = () => {
  try {
    const historyStr = localStorage.getItem(DRAFT_STORAGE.HISTORY_KEY);
    return historyStr ? JSON.parse(historyStr) : [];
  } catch (e) {
    console.error('[DraftStorage] Failed to get history:', e);
    return [];
  }
};

export default {
  saveDraftSession,
  loadDraftSession,
  getDraftSummary,
  clearDraftSession,
  hasSavedSession,
  restoreDraftSession,
  addToHistory,
  getHistory,
};
